import React, { useState, useEffect } from 'react';
import { cartService, CartItem } from '../services/cart';
import { Box, Typography, IconButton, Button, List, ListItem, ListItemAvatar, Avatar, ListItemText, TextField, Divider, Paper } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface ImageCell {
  image: string;
  width: number;
  height: number;
}

const fetchImageAsDataUrl = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const getImageUrl = (imageUrl: string | undefined | null) => {
  const defaultPlaceholder = 'https://placehold.co/100x100?text=No+Image';
  try {
    if (!imageUrl) return defaultPlaceholder;
    if (imageUrl.includes('s3.amazonaws.com')) return imageUrl;
    if (imageUrl.startsWith('http')) return imageUrl;
    const cleanUrl = imageUrl
      .replace(/['"]/g, '')
      .replace(/;/g, '')
      .replace(/^\/+/g, '')
      .replace(/\/+/g, '/')
      .trim();
    let filename = cleanUrl;
    const patterns = [
      /^uploads\/products\/(.+)$/, /^products\/(.+)$/, /^app\/uploads\/products\/(.+)$/, /^(.+)$/
    ];
    for (const pattern of patterns) {
      const match = cleanUrl.match(pattern);
      if (match && match[1]) {
        filename = match[1];
        break;
      }
    }
    filename = filename.replace(/[^\w\-.]/g, '').replace(/\.+/g, '.').toLowerCase();
    if (!filename || filename.length < 5) return defaultPlaceholder;
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://skylightbackend-production.up.railway.app';
    const finalUrl = `${apiBaseUrl}/uploads/products/${filename}`;
    try {
      new URL(finalUrl);
      return finalUrl;
    } catch {
      return defaultPlaceholder;
    }
  } catch {
    return defaultPlaceholder;
  }
};

// Attempt to construct a public S3 URL using optional envs
const buildS3UrlFromKey = (key: string | undefined | null): string | null => {
  if (!key) return null;
  if (key.startsWith('http')) return key;
  const cleaned = key.replace(/^\/+/, '').trim();
  if (!cleaned) return null;

  const explicitBase = process.env.REACT_APP_S3_PUBLIC_BASE; // e.g. https://mybucket.s3.ap-south-1.amazonaws.com
  if (explicitBase) {
    try {
      const url = `${explicitBase.replace(/\/$/, '')}/${cleaned}`;
      console.log('[PDF][image] Using REACT_APP_S3_PUBLIC_BASE to build URL:', { url });
      new URL(url);
      return url;
    } catch {}
  }

  const bucket = process.env.REACT_APP_AWS_S3_BUCKET;
  const region = process.env.AWS_REGION;
  console.log('[PDF][image] Env for S3 build:', { bucket, region, hasBucket: !!bucket, hasRegion: !!region });
  if (bucket && region) {
    const url = `https://${bucket}.s3.${region}.amazonaws.com/${cleaned}`;
    try {
      console.log('[PDF][image] Built S3 URL from bucket/region:', { url });
      new URL(url);
      return url;
    } catch {}
  }
  return null;
};

// Resolve best data URL for PDF with multiple fallbacks
const resolveImageDataUrl = async (rawUrl: string | undefined | null): Promise<string | null> => {
  const candidates: string[] = [];

  // 1) Use backend-served uploads path (or pass-through for full URLs)
  try {
    const u = getImageUrl(rawUrl);
    console.log('[PDF][image] getImageUrl result:', { rawUrl, resolved: u });
    if (u) candidates.push(u);
  } catch {}

  // 2) If the original was already an absolute URL, include it explicitly as well
  if (rawUrl && typeof rawUrl === 'string' && rawUrl.startsWith('http')) {
    console.log('[PDF][image] Adding original absolute URL as candidate:', { rawUrl });
    candidates.push(rawUrl);
  }

  // 3) If we only have a key, try building an S3 URL if envs are provided
  const s3 = buildS3UrlFromKey(rawUrl || undefined);
  if (s3) candidates.push(s3);

  // Deduplicate while preserving order
  const seen = new Set<string>();
  const uniqueCandidates = candidates.filter((u) => {
    if (seen.has(u)) return false;
    seen.add(u);
    return true;
  });

  console.log('[PDF][image] Candidate URLs for image fetch:', uniqueCandidates);

  for (const url of uniqueCandidates) {
    try {
      const data = await fetchImageAsDataUrl(url);
      if (data) {
        console.log('[PDF][image] Successfully fetched and converted image to base64 from:', { url });
        return data;
      }
      console.warn('[PDF][image] Fallback: fetch returned null for URL:', { url });
    } catch (e) {
      console.error('[PDF][image] Error fetching image candidate:', { url, error: (e as any)?.message || e });
    }
  }
  return null;
};

const Cart: React.FC = () => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [updateFlag, setUpdateFlag] = useState(false); // To force re-render on update

  useEffect(() => {
    setCart(cartService.getCart());
  }, [updateFlag]);

  const handleQuantityChange = (productId: number, quantity: number) => {
    cartService.updateQuantity(productId, quantity);
    setUpdateFlag(flag => !flag);
  };

  const handleRemove = (productId: number) => {
    cartService.removeFromCart(productId);
    setUpdateFlag(flag => !flag);
  };

  const handleClear = () => {
    cartService.clearCart();
    setUpdateFlag(flag => !flag);
  };

  const handleDownloadPDF = async () => {
    // Create a clean A4 portrait document in points for precise layout
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;

    // Header
    const title = 'Cart Summary';
    const date = new Date().toLocaleString();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(title, margin, margin);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Generated: ${date}`, margin, margin + 16);

    // Prepare table data
    const tableBody: any[] = [];
    for (let i = 0; i < cart.length; i++) {
      const item = cart[i];
      const price = typeof item.product.price === 'string' ? parseFloat(item.product.price) : item.product.price;
      const subtotal = price * item.quantity;

      // Resolve product image robustly for PDF (tries backend URL, original URL, optional S3 URL)
      console.log('[PDF][image] Resolving product image for item:', { name: item.product.name, imageUrl: item.product.imageUrl });
      const imgData: string | null = await resolveImageDataUrl(item.product.imageUrl);
      console.log('[PDF][image] Resolution result:', { hasImage: !!imgData });
      const imageCell: ImageCell | '' = imgData ? { image: imgData, width: 42, height: 42 } : '';
      tableBody.push([
        imageCell,
        item.product.name,
        `₹${price.toFixed(2)}`,
        item.quantity.toString(),
        `₹${subtotal.toFixed(2)}`,
      ]);
    }

    // Build table
    autoTable(doc, {
      startY: margin + 30,
      head: [['Image', 'Product', 'Unit Price', 'Qty', 'Subtotal']],
      body: tableBody,
      theme: 'grid',
      styles: { cellPadding: 6, fontSize: 10, valign: 'middle' },
      headStyles: { fillColor: [66, 165, 245], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 56, halign: 'center' },
        1: { cellWidth: pageWidth - margin * 2 - (56 + 80 + 40 + 90) },
        2: { cellWidth: 80, halign: 'right' },
        3: { cellWidth: 40, halign: 'center' },
        4: { cellWidth: 90, halign: 'right' },
      },
      margin: { left: margin, right: margin },
      didDrawCell: (data) => {
        const cell = data.cell.raw as ImageCell | '';
        if (
          data.section === 'body' &&
          data.column.index === 0 &&
          cell &&
          typeof cell === 'object' &&
          'image' in cell
        ) {
          const x = data.cell.x + (data.cell.width - (cell as ImageCell).width) / 2;
          const y = data.cell.y + (data.cell.height - (cell as ImageCell).height) / 2;
          const format = (cell as ImageCell).image.startsWith('data:image/png') ? 'PNG' : 'JPEG';
          doc.addImage((cell as ImageCell).image, format, x, y, (cell as ImageCell).width, (cell as ImageCell).height);
        }
      },
    });

    // Summary (Total)
    const finalY = (doc as any).lastAutoTable.finalY || margin + 30;
    const total = cart.reduce((sum, item) => {
      const price = typeof item.product.price === 'string' ? parseFloat(item.product.price) : item.product.price;
      return sum + price * item.quantity;
    }, 0);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`Total: ₹${total.toFixed(2)}`, pageWidth - margin - 120, finalY + 24, { align: 'left' });

    // Footer page number
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 16, { align: 'right' });
    }

    doc.save('cart.pdf');
  };

  const total = cart.reduce((sum, item) => {
    const price = typeof item.product.price === 'string' ? parseFloat(item.product.price) : item.product.price;
    return sum + price * item.quantity;
  }, 0);

  return (
    <Paper sx={{ maxWidth: 600, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="primary" onClick={handleDownloadPDF} disabled={cart.length === 0}>
          Download PDF
        </Button>
      </Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Your Cart</Typography>
      {cart.length === 0 ? (
        <Typography color="text.secondary">Your cart is empty.</Typography>
      ) : (
        <>
          <List>
            {cart.map(item => (
              <React.Fragment key={item.product.id}>
                <ListItem alignItems="flex-start" secondaryAction={
                  <IconButton edge="end" aria-label="delete" onClick={() => handleRemove(item.product.id)}>
                    <DeleteIcon />
                  </IconButton>
                }>
                  <ListItemAvatar>
                    <Avatar src={item.product.imageUrl} alt={item.product.name} variant="square" />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.product.name}
                    secondary={`₹${(typeof item.product.price === 'string' ? parseFloat(item.product.price) : item.product.price).toFixed(2)}`}
                  />
                  <TextField
                    type="number"
                    size="small"
                    value={item.quantity}
                    inputProps={{ min: 1, style: { width: 48 } }}
                    onChange={e => handleQuantityChange(item.product.id, Math.max(1, Number(e.target.value)))}
                    sx={{ ml: 2 }}
                  />
                  <Typography sx={{ ml: 2, minWidth: 60, textAlign: 'right' }}>
                    ₹{((typeof item.product.price === 'string' ? parseFloat(item.product.price) : item.product.price) * item.quantity).toFixed(2)}
                  </Typography>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Button color="error" onClick={handleClear}>Clear Cart</Button>
            <Typography variant="h6">Total: ₹{total.toFixed(2)}</Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default Cart; 